import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { WorkPackageList } from '@/components/soft-delete/WorkPackageList';
import { Spinner } from '@/components/ui/spinner';

import type { BulkDamageDeletePlan } from './bulk-damage-delete';

type BulkDamageDeleteSummaryProps = {
	plan: BulkDamageDeletePlan;
	isLoading: boolean;
	isError: boolean;
	/** The damage's title for the list of blocked damages */
	titleOf: (id: string) => string;
};

/** Bulk-delete confirmation body for damages: the damages left out and why (GH #331). */
export function BulkDamageDeleteSummary({ plan, isLoading, isError, titleOf }: BulkDamageDeleteSummaryProps) {
	const { t } = useTranslation();
	if (isLoading) {
		return (
			<span className="flex items-center gap-2 text-sm text-muted-foreground">
				<Spinner className="size-4" /> {t('common.deleted.previewLoading')}
			</span>
		);
	}
	if (isError) {
		return <p className="text-sm text-muted-foreground">{t('common.deleted.previewFailed')}</p>;
	}
	return (
		<div className="space-y-3 text-sm">
			{plan.blocked.length > 0 && (
				<div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-2">
					<p className="flex items-start gap-2 font-medium text-destructive">
						<TriangleAlert className="mt-0.5 size-4 shrink-0" />
						{t('common.bulk.blockedDamages', { count: plan.blocked.length })}
					</p>
					<ul className="max-h-48 space-y-1 overflow-y-auto pl-6">
						{plan.blocked.map(({ id, workPackages }) => (
							<li key={id}>
								<span className="font-medium">{titleOf(id)}</span>{' '}
								{/* Same ID as on the damage page, so the user can find it */}
								<code className="rounded bg-muted/50 px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
									{t('damages.popup.idLabel', { id })}
								</code>
								<WorkPackageList workPackages={workPackages} showLines={false} />
							</li>
						))}
					</ul>
				</div>
			)}
			{plan.unavailableIds.length > 0 && (
				<p className="text-muted-foreground">{t('common.bulk.unavailable', { count: plan.unavailableIds.length })}</p>
			)}
		</div>
	);
}
